// useMarketerConsultantPage.js — 마케터 1:1 관리 화면의 데이터·창 상태·저장 처리
//   2026-10-06 AdminMarketerConsultantPage.jsx(386줄)에서 글자 그대로 옮겼다(동작 변화 0). PC·휴대폰 화면이 같이 쓴다.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { todayKST } from '../../../utils/date';
import {
  EMPTY_FORM, normalizeMoney, normalizeCompanies, withIntegratedMenu,
} from './marketerConsultantPageUtils';

export function useMarketerConsultantPage({
  availableCompanies = null,
  integratedMenuId = null,
  fixedCompanyCode = null,
  fixedCompanyName = null,
}) {
  const { showToast } = useToast();
  const fixedCode = fixedCompanyCode ? String(fixedCompanyCode).trim().toUpperCase() : null;
  const requestSeqRef = useRef(0);
  const [companies, setCompanies] = useState([{ code: 'PINE', name: 'PINE' }]);
  const [activeTab, setActiveTab] = useState(() => fixedCode || 'PINE');
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [moveModal, setMoveModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [moveErr, setMoveErr] = useState('');

  useEffect(() => {
    if (fixedCode) {
      const nextCompanies = normalizeCompanies([{
        code: fixedCode,
        name: fixedCompanyName || fixedCode,
      }]);
      setCompanies(nextCompanies);
      setActiveTab(nextCompanies[0].code);
      return;
    }

    if (Array.isArray(availableCompanies) && availableCompanies.length > 0) {
      const nextCompanies = normalizeCompanies(availableCompanies);
      setCompanies(nextCompanies);
      setActiveTab((prev) => (
        nextCompanies.some((company) => company.code === prev) ? prev : nextCompanies[0].code
      ));
      return;
    }

    (async () => {
      const res = await apiClient.get('/api/admin/external-companies');
      if (!res.ok || !Array.isArray(res.data?.list)) return;
      setCompanies([
        { code: 'PINE', name: 'PINE' },
        ...res.data.list.map((company) => ({ code: company.code, name: company.name || company.code })),
      ]);
    })();
  }, [availableCompanies, fixedCode, fixedCompanyName]);

  const loadData = useCallback(async (companyCode) => {
    const requestedCompanyCode = String(companyCode || 'PINE').trim().toUpperCase();
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setLoading(true);
    const [assignmentRes, userRes] = await Promise.all([
      apiClient.get(withIntegratedMenu(`/api/admin/marketer-consultant/assignments?company_code=${requestedCompanyCode}`, integratedMenuId)),
      apiClient.get(withIntegratedMenu(`/api/admin/marketer-consultant/users?company_code=${requestedCompanyCode}`, integratedMenuId)),
    ]);
    if (requestSeq !== requestSeqRef.current) return;
    setLoading(false);

    if (assignmentRes.ok) setAssignments(Array.isArray(assignmentRes.data) ? assignmentRes.data : []);
    else showToast('배정 목록을 불러오지 못했습니다.', 'error');

    if (userRes.ok) setUsers(Array.isArray(userRes.data) ? userRes.data : []);
    else showToast('직원 목록을 불러오지 못했습니다.', 'error');
  }, [integratedMenuId, showToast]);

  useEffect(() => { loadData(activeTab); }, [activeTab, loadData]);

  // 보드 기준월 = 오늘이 속한 달 (2026-09-02 사용자 확정).
  //   이전에는 급여 지급일 규칙의 default-month(지급일 전엔 전월)를 썼는데, 9월 초에 9/1 시작
  //   배정이 '예정'으로 보이는 혼란을 만들었다. 청구 검증 패널(MonthlyCalculationPanel)은
  //   자체적으로 default-month API를 불러 급여 규칙 월을 유지한다 — 정산·확정 흐름 무영향.
  const boardMonth = todayKST().slice(0, 7);

  const marketers = useMemo(() => users.filter((user) => user.role_type === 'marketer'), [users]);
  const consultants = useMemo(() => users.filter((user) => user.role_type === 'consultant'), [users]);
  const activeCompanyName = companies.find((company) => company.code === activeTab)?.name || activeTab;
  const isIntegratedMode = Boolean(integratedMenuId);
  const shouldShowCompanyTabs = companies.length > 1;

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function openAdd(marketer, consultant = null) {
    const lockedMarketer = marketer ? { id: Number(marketer.id), name: marketer.name } : null;
    setModal({ mode: 'add', lockedMarketer });
    setForm({
      ...EMPTY_FORM,
      marketer_user_id: lockedMarketer ? String(lockedMarketer.id) : '',
      consultant_user_id: consultant ? String(consultant.id) : '',
      start_date: todayKST(),
    });
    setFormErr('');
  }

  function openEdit(assignment) {
    setModal({
      mode: 'edit',
      assignment,
      lockedMarketer: {
        id: Number(assignment.marketer_user_id),
        name: assignment.marketer_name || '마케터',
      },
    });
    setForm({
      marketer_user_id: String(assignment.marketer_user_id || ''),
      consultant_user_id: String(assignment.consultant_user_id || ''),
      start_date: assignment.start_date?.slice(0, 10) || '',
      end_date: assignment.end_date?.slice(0, 10) || '',
      monthly_salary: '',
      notes: assignment.notes || '',
    });
    setFormErr('');
  }

  function closeModal() {
    setModal(null);
    setFormErr('');
  }

  function openMove(assignment, targetMarketer) {
    setMoveModal({ mode: 'move', assignment, targetMarketer });
    setMoveErr('');
  }

  function openEnd(assignment) {
    setMoveModal({ mode: 'end', assignment, targetMarketer: null });
    setMoveErr('');
  }

  function closeMoveModal() {
    setMoveModal(null);
    setMoveErr('');
  }

  async function handleSave() {
    setFormErr('');
    if (!form.marketer_user_id || !form.consultant_user_id || !form.start_date) {
      setFormErr('마케터, 1:1 직원, 시작일을 모두 골라 주세요.');
      return;
    }

    const body = {
      company_code: activeTab,
      marketer_user_id: Number(form.marketer_user_id),
      consultant_user_id: Number(form.consultant_user_id),
      start_date: form.start_date,
      end_date: form.end_date || null,
      notes: form.notes || null,
    };
    if (integratedMenuId) body.integrated_menu_id = integratedMenuId;
    if (modal?.mode !== 'edit') {
      body.monthly_salary = normalizeMoney(form.monthly_salary);
    }

    setSaving(true);
    const res = modal?.mode === 'edit'
      ? await apiClient.put(withIntegratedMenu(`/api/admin/marketer-consultant/assignments/${modal.assignment.id}`, integratedMenuId), body)
      : await apiClient.post('/api/admin/marketer-consultant/assignments', body);
    setSaving(false);

    if (!res.ok) {
      setFormErr(res.data?.message || '저장에 실패했습니다.');
      return;
    }
    showToast(modal?.mode === 'edit' ? '배정을 수정했습니다.' : '배정을 등록했습니다.', 'success');
    closeModal();
    await loadData(activeTab);
  }

  async function handleDelete(id) {
    if (!window.confirm('이 1:1 배정을 삭제할까요?')) return;
    const res = await apiClient.delete(withIntegratedMenu(`/api/admin/marketer-consultant/assignments/${id}`, integratedMenuId));
    if (!res.ok) {
      showToast(res.data?.message || '삭제에 실패했습니다.', 'error');
      return;
    }
    showToast('배정을 삭제했습니다.', 'success');
    await loadData(activeTab);
  }

  async function handleMoveSave(payload) {
    if (!moveModal?.assignment) return;
    setMoveErr('');
    const isEnd = moveModal.mode === 'end';
    const body = isEnd
      ? payload
      : { ...payload, target_marketer_user_id: Number(moveModal.targetMarketer?.id) };
    if (integratedMenuId) body.integrated_menu_id = integratedMenuId;
    if (!isEnd && !body.target_marketer_user_id) {
      setMoveErr('새 담당 마케터를 찾을 수 없습니다.');
      return;
    }

    setSaving(true);
    const res = await apiClient.post(
      withIntegratedMenu(`/api/admin/marketer-consultant/assignments/${moveModal.assignment.id}/${isEnd ? 'end' : 'move'}`, integratedMenuId),
      body,
    );
    setSaving(false);

    if (!res.ok) {
      setMoveErr(res.data?.message || (isEnd ? '배정 끝내기에 실패했습니다.' : '담당 마케터 바꾸기에 실패했습니다.'));
      return;
    }
    showToast(isEnd ? '배정을 끝냈습니다.' : '담당 마케터를 바꿨습니다.', 'success');
    closeMoveModal();
    await loadData(activeTab);
  }

  // 변경 기록 창은 useChangeHistory 로 옮김(2026-10-06 UX 5단계 — 회사 전체·배정 하나 두 가지)
  return {
    companies,
    activeTab,
    setActiveTab,
    assignments,
    loading,
    modal,
    moveModal,
    form,
    saving,
    formErr,
    moveErr,
    loadData,
    boardMonth,
    marketers,
    consultants,
    activeCompanyName,
    isIntegratedMode,
    shouldShowCompanyTabs,
    setField,
    openAdd,
    openEdit,
    closeModal,
    openMove,
    openEnd,
    closeMoveModal,
    handleSave,
    handleDelete,
    handleMoveSave,
  };
}
